// apps/roleforge-app/src/lib/sse-event-router.ts
//
// Plan 3e-i — Phase 2.2.
//
// Multiplexed SSE event router. ConverseEvents stream from one /converse SSE
// endpoint; this router splits them into three surfaces (chat, operating_model,
// activity) and dispatches to per-(surface, kind) subscribers.
//
// Activity surface uses a minimum-display-duration queue so quick successive
// "Saved! / Drafting… / Saved!" events don't strobe the right panel — each
// item gets at least N ms of screen time before the next one fires.

import type {
  ConverseEvent,
  OperatingModelEventPayload,
  ActivityEventPayload,
} from './converse-types';

type Surface = 'operating_model' | 'activity';

type OperatingModelKind = OperatingModelEventPayload['kind'];
type ActivityKind = ActivityEventPayload['kind'];

type OperatingModelHandler = (ev: { type: 'operating_model_event'; surface: 'operating_model'; payload: OperatingModelEventPayload }) => void;
type ActivityHandler = (ev: { type: 'activity_event'; surface: 'activity'; payload: ActivityEventPayload }) => void;
type ChatHandler = (ev: ConverseEvent) => void;

export interface SseEventRouterOptions {
  /** Minimum display duration per activity event, in ms. Default 1500. */
  activityMinDisplayMs?: number;
}

export interface SseEventRouter {
  dispatch(ev: ConverseEvent): void;
  subscribe(
    surface: 'operating_model',
    kind: OperatingModelKind,
    handler: OperatingModelHandler,
  ): () => void;
  subscribe(
    surface: 'activity',
    kind: ActivityKind,
    handler: ActivityHandler,
  ): () => void;
  subscribeChat(handler: ChatHandler): () => void;
}

export function createSseEventRouter(opts: SseEventRouterOptions = {}): SseEventRouter {
  const minDisplay = opts.activityMinDisplayMs ?? 1500;

  const opSubs = new Map<OperatingModelKind, Set<OperatingModelHandler>>();
  const activitySubs = new Map<ActivityKind, Set<ActivityHandler>>();
  const chatSubs = new Set<ChatHandler>();

  const activityQueue: Array<{ type: 'activity_event'; surface: 'activity'; payload: ActivityEventPayload }> = [];
  let activityNextAvailableAt = 0;
  let activityTimer: ReturnType<typeof setTimeout> | null = null;

  function flushOneActivity() {
    activityTimer = null;
    if (activityQueue.length === 0) return;
    const ev = activityQueue.shift()!;
    fireActivity(ev);
    activityNextAvailableAt = Date.now() + minDisplay;
    if (activityQueue.length > 0) {
      activityTimer = setTimeout(flushOneActivity, minDisplay);
    }
  }

  function fireActivity(ev: { type: 'activity_event'; surface: 'activity'; payload: ActivityEventPayload }) {
    const subs = activitySubs.get(ev.payload.kind);
    if (!subs) return;
    for (const h of subs) {
      try { h(ev); } catch { /* swallow — never break the stream */ }
    }
  }

  function enqueueActivity(ev: { type: 'activity_event'; surface: 'activity'; payload: ActivityEventPayload }) {
    const now = Date.now();
    if (now >= activityNextAvailableAt && activityQueue.length === 0 && activityTimer === null) {
      // First event of a quiet period — fire immediately, gate the next.
      fireActivity(ev);
      activityNextAvailableAt = now + minDisplay;
      return;
    }
    activityQueue.push(ev);
    if (activityTimer === null) {
      const wait = Math.max(0, activityNextAvailableAt - now);
      activityTimer = setTimeout(flushOneActivity, wait);
    }
  }

  return {
    dispatch(ev) {
      if (!ev || typeof (ev as { type?: unknown }).type !== 'string') return;

      // Chat surface: every event flows here so chat consumer can also see
      // tool_use / done / error frames if it cares.
      for (const h of chatSubs) {
        try { h(ev); } catch { /* swallow */ }
      }

      if (ev.type === 'operating_model_event') {
        const subs = opSubs.get(ev.payload.kind);
        if (!subs) return;
        for (const h of subs) {
          try { h(ev); } catch { /* swallow */ }
        }
      } else if (ev.type === 'activity_event') {
        enqueueActivity(ev);
      }
    },

    subscribe(surface: Surface, kind: string, handler: unknown) {
      if (surface === 'operating_model') {
        const k = kind as OperatingModelKind;
        let set = opSubs.get(k);
        if (!set) { set = new Set(); opSubs.set(k, set); }
        set.add(handler as OperatingModelHandler);
        return () => set!.delete(handler as OperatingModelHandler);
      }
      const k = kind as ActivityKind;
      let set = activitySubs.get(k);
      if (!set) { set = new Set(); activitySubs.set(k, set); }
      set.add(handler as ActivityHandler);
      return () => set!.delete(handler as ActivityHandler);
    },

    subscribeChat(handler) {
      chatSubs.add(handler);
      return () => chatSubs.delete(handler);
    },
  } as SseEventRouter;
}
